import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where,
  updateDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const GA4thEducatorSocialStudies = () => {
  const navigate = useNavigate();
  const [popupContent, setPopupContent] = useState(null);
  const [visibleTopics, setVisibleTopics] = useState(() => {
    const savedVisibility = localStorage.getItem("visibleTopicsSocialStudies");
    return savedVisibility ? JSON.parse(savedVisibility) : Array(27).fill(true);
  });
  
  const [formVisible, setFormVisible] = useState(false);
  const [educatorControlsVisible, setEducatorControlsVisible] = useState(false);
  const [students, setStudents] = useState([]);
  const [user, setUser] = useState(null);
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) {
          setUser({ ...userSnap.data(), uid: currentUser.uid });
        } else {
          console.error("No such user found in Firestore!");
        }
      } else {
        console.error("No user is signed in.");
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  useEffect(() => {
    if (!user?.uid) return; // Ensure user UID exists before making a request
  
    const fetchEducatorSettings = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userDocRef);
  
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Ensure visibility settings are retrieved from Firestore
          const storedVisibility = userData.sstools?.map(tool => tool.visible) || Array(27).fill(true);
          setVisibleTopics(storedVisibility);
  
          // Sync with localStorage for persistence across sessions
          localStorage.setItem("visibleTopicsSocialStudies", JSON.stringify(storedVisibility));
        } else {
          console.error("Educator settings not found!");
        }
      } catch (error) {
        console.error("Error fetching educator settings:", error);
      }
    };
  
    fetchEducatorSettings();
  }, [user]); // Runs when the user state updates
  

  const handleCheckboxChange = (index) => {
    const updatedVisibleTopics = [...visibleTopics];
    updatedVisibleTopics[index] = !updatedVisibleTopics[index];
    setVisibleTopics(updatedVisibleTopics);
    localStorage.setItem("visibleTopicsSocialStudies", JSON.stringify(updatedVisibleTopics));

  };

  const handleSaveChanges = async () => {
    if (!user || !user.uid) {
      console.error("User UID is not defined.");
      return;
    }
  
    if (!topics || !Array.isArray(topics)) {
      console.error("Topics is not defined or not an array.");
      return;
    }
  
    const updatedTools = topics.map((topic, index) => ({
      title: topic.title,
      visible: visibleTopics[index] ?? true,  // Ensure visibleTopics[index] exists
    }));
  
    try {
      const userDocRef = doc(db, "users", user.uid); // Ensure user.uid is defined
      await updateDoc(userDocRef, { sstools: updatedTools });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };
  

  const handleOpenPopup = (content) => {
    setPopupContent(content);
  };

  const handleClosePopup = () => {
    setPopupContent(null);
  };

  const handleHomeClick = () => {
    if (user) {
      const role = user.role.toLowerCase();
      if (role.includes("student")) {
        navigate("/dashboard");
      } else if (role.includes("educator")) {
        navigate("/educator/dashboard");
      } else if (role.includes("admin") || role.includes("administrator")) {
        navigate("/admin");
      } else {
        console.error("Unknown user role:", role);
      }
    } else {
      console.error("User is not defined.");
    }
  };

  const handleSubjectClick = (subject) => {
    const role = "ga-4th-educator"; // Replace with actual role logic
    const path = subject === "socialstudies" 
      ? `/educator/ga/ga-4th-educator-socialstudies` 
      : `/educator/ga/ga-4th-educator-${subject}`;
    navigate(path);
  };

  const handleRequestClick = () => {
    setFormVisible(true);
  };

  const handleFormClose = () => {
    setFormVisible(false);
  };

  const handleEducatorControlsClick = () => {
    setEducatorControlsVisible(true);
  };

  const handleEducatorControlsClose = () => {
    setEducatorControlsVisible(false);
  };

  const handleStudentProgressClick = async () => {
    if (!user || !user.educatorCode) {
      console.error("No logged-in user or educator code found.");
      return;
    }
  
    try {
      const q = query(
        collection(db, "users"),
        where("role", "==", "ga-4th-student"),
        where("educatorCode", "==", user.educatorCode)
      );
  
      const querySnapshot = await getDocs(q);
      const matchingUsers = querySnapshot.docs.map(doc => doc.data());
  
      setPopupContent(
        <div>
          <h2 style={{ color: "black" }}>Student Progress</h2>
          {user && (
            <>
              <h2 style={{ color: "black" }}>{user.sirName} {user.firstName} {user.lastName}'s Class</h2>
              <h3>Educator Code: {user.educatorCode}</h3>
            </>
          )}
          <h2>Student List</h2>
          <div style={styles.studentButtons}>
            {matchingUsers.length === 0 ? (
              <p>No students found with the specified criteria.</p>
            ) : (
              matchingUsers.map((student, index) => (
                <button
                  key={index}
                  style={{
                    ...styles.studentButton,
                    backgroundColor: `hsl(${(index * 40) % 360}, 70%, 80%)`,
                  }}
                  onClick={() => handleStudentClick(student.firstName)}
                >
                  {student.firstName}
                </button>
              ))
            )}
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error fetching students:", error);
      setPopupContent(<p>Error loading students.</p>);
    }
  };

  const handleStudentClick = (firstName) => {
    alert(`It's me ${firstName}!`);
  };

  const handleGameClick = (gameUrl) => {
    if (gameUrl) {
      navigate(`/${gameUrl}`); // Ensure the URL is correctly prefixed with a slash
    } else {
      console.error("Game URL is not defined.");
    }
  };
  // Background image URL (set this to null or empty string to use the default color)
  const backgroundImage = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbg-texture-light-green.png?alt=media&token=8145bbbd-edab-4100-9483-7a4c24688ef2"; // Replace with actual background image URL

  const topics = [
    
    {
      title: "--George Washington Carver--",
      subtitle1: "",
      standardNumber: "2.B.1",
      description: "Understand how values and beliefs shape culture in America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fgeorgewashingtoncarver2025video.mp4?alt=media&token=9716d58a-ab18-4161-96e2-5ff28839b30a",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fgeorgewashingtoncarver2025song.mp3?alt=media&token=382931f7-2833-4ebe-a316-273c054f4c1a",
      lyrics: [
        "(Chorus)",
"George Washington Carver was The Peanut Man.",
"A great man he was.",
"George Washington Carver was The Peanut Man.",
"A great man he was.",

"(Verse 1)",
"George Washington Carver has an incredible story,",
"He was born into slavery, in Missouri.",
"Eager to learn, he moved around,",
"Paying for school in every town.",
"At Iowa State, he found his place,",
"A master's degree, he earned with grace.",
"A brilliant mind with a humble heart,",
"In the world of science, he made his start.",
"With peanuts, sweet potatoes, and plants galore,",
"He showed the farmers how to grow more.",

"(Chorus)",
"George Washington Carver was The Peanut Man.",
"A great man he was.",
"George Washington Carver was The Peanut Man.",
"A great man he was.",

"(Verse 2)",
"George Washington Carver taught southern farmers,",
"How to plant other plants besides cotton.",
"He introduced crop rotation,",
"To save the soil from devastation.",
"He discovered hundreds of uses for the peanut,",
"From dyes and plastics to a tasty nut.",
"His innovations helped many survive,",
"Bringing new hope, keeping dreams alive.",

"(Chorus)",
"George Washington Carver was The Peanut Man.",
"A great man he was.",
"George Washington Carver was The Peanut Man.",
"A great man he was.",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "games/nc-games/georgewashingtoncarver-game2",
      titleImage: "", // Motion image URL
    },
    {
      title: "--Susan B. Anthony--",
      subtitle1: "",
      standardNumber: "2.B.1",
      description: "Understand how values and beliefs shape culture in America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fsusanbanthony2025video.mp4?alt=media&token=6cd6fce4-7ef3-4ed4-8e29-b216c404e70d",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fsusanbanthony2025song.mp3?alt=media&token=ec7d574a-2ec3-46a0-9fac-10c200acb850",
      lyrics: [
        "(chorus)",
"Gave speeches to end slavery",
"and fought for alcohol abuse.",
"To help women get equal rights, she took on that issue.",
"Gave speeches to end slavery",
"and fought for alcohol abuse.",
"To help women get equal rights, she took on that issue.",

"(verse)",
"Susan B. Anthony was born February 15, 1820.",
"Susan B. Anthony was born February 15, 1820.",
"Susan fought for women to have the same rights as men.",
"Susan fought for women to have the same rights as men.",
"Susan fought for women to have the right to vote.",
"Women voting in those times were illegal.",
"Susan fought for civil rights; Susan fought for civil rights.",
"Susan fought for women’s rights; Susan fought for women’s rights.",

"(chorus)",
"Gave speeches to end slavery",
"and fought for alcohol abuse.",
"To help women get equal rights, she took on that issue.",
"Gave speeches to end slavery",
"and fought for alcohol abuse.",
"To help women get equal rights, she took on that issue.",

"© Smart Board Learning, LLC. All rights reserved.",
      ],
      game: "games/nc-games/susanbanthony-game2",
      titleImage: "", // Motion image URL
    },
    {
      title: "--The Declaration--",
      subtitle1: "",
      standardNumber: "2.C&G.1",
      description: "Understand how freedom, equality, and democracy contribute to the government of America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fthedeclaration2025video.mp4?alt=media&token=5f196d84-24ce-4d49-9fc7-a5e1edf1040f",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fthedeclaration2025song.mp3?alt=media&token=a3535b3a-c748-41af-be89-0269139b63f8",
      lyrics: ["(Chorus)",
"The Declaration of Independence.",
"The Declaration of Independence.",

"(Verse)",
"All the reasons we have our independence,",
"Was written in The Declaration of Independence.",
"All the ideas we have for self-government,",
"Was written in The Declaration of Independence.",

"All people are created equal.",
"All people have rights to life.",
"All people have rights to liberty,",
"And the pursuit of happiness.",

"Thomas Jefferson was an author,",
"Of The Declaration of Independence.",

"(Chorus)",
"The Declaration of Independence.",
"The Declaration of Independence.",
"The Declaration of Independence.",
"The Declaration of Independence.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/thedeclaration-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Government Branches--",
      subtitle1: "",
      standardNumber: "2.C&G.1",
      description: "Understand how freedom, equality, and democracy contribute to the government of America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fbranchesofgovernment2025video.mp4?alt=media&token=e7fbc765-6133-4991-a830-f784b10ed39d",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fbranchesofgovernment2025song.mp3?alt=media&token=1f88e28e-1590-4324-83ce-37172a75562a",
      lyrics: ["(Chorus)",
"This is our government, this is our government.",
"This is where we live.",
"Three parts of government",
"Executive, Judicial, and Legislative.",

"(Verse 1)",
"The Executive branch is the President.",
"The Executive branch is the President.",
"Qualifications for Presidency",
"Is 35 years of age, at least.",
"A natural born citizen born here.",
"Must reside in the states for 14 years.",
"Now, the functions of the President,",
"Chief of State, head of Government.",
"Resides in the White House.",
"Commander-in-Chief of our military, our house.",
"Greet leaders of other nations.",
"Help create treaties and other negotiations.",
"Four-year term, only allowed two.",
"Can sign a bill into law and veto bills too.",
"The President’s veto can be overturned",
"By two-thirds of the Senate and the House of Representatives.",

"(Chorus)",
"This is our government, this is our government.",
"This is where we live.",
"Three parts of government",
"Executive, Judicial, and Legislative.",

"(Verse 2)",
"The Legislative branch is made up of Congress,",
"And they make the laws.",
"Regulate commerce, tax the people,",
"Borrow money, and declare the wars.",
"The Legislative branch has two parts.",
"The Senate and the House of Representatives.",
"Now, the House of Representatives",
"Must live in the state they are representing.",
"Elected every two years to fill 435 seats.",
"Must be a citizen for 7 years at least.",
"25 years of age.",
"The number of reps depends on state populations.",
"Now, the Senate, at least 9 years must be a citizen,",
"Live in the state they are representing.",
"Must be 30, 6-year terms.",
"Every 2 years elect one-third.",
"The Vice President is the President of the Senate.",
"100 Senators, 2 per state.",
"The Vice President is the President of the Senate.",
"100 Senators, 2 per state.",

"(Chorus)",
"This is our government, this is our government.",
"This is where we live.",
"Three parts of government",
"Executive, Judicial, and Legislative.",

"(Verse 3)",
"The Judicial branch is made up of all courts.",
"The highest court is the Supreme Court.",
"It's on Congress to decide",
"How many Justices there should be. Now it's nine.",
"The President chooses",
"Who will be the Chief Justices and the Senate approves it.",
"Once appointed Justice,",
"You are there until death or retirement.",
"The Supreme Court is a guide for all the courts in our country.",
"Judicial review began in 1803,",
"But that's not all,",
"The Supreme Court had the final say in the law.",

"(Chorus)",
"This is our government, this is our government.",
"This is where we live.",
"Three parts of government",
"Executive, Judicial, and Legislative.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/governmentbranches-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Tribal Government--",
      subtitle1: "",
      standardNumber: "2.C&G.1",
      description: "Understand how freedom, equality, and democracy contribute to the government of America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fnctribalgovernment2025video.mp4?alt=media&token=e2778deb-ca9b-4f99-bfb1-7270d5115a2e",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fnctribalgovernment2025song.mp3?alt=media&token=57e24b09-1b13-4f52-af16-a4890137ff16",
      lyrics: ["(chorus)",
"The Tribal Government, Tribal Government.",
"We’re free because we fought.",
"Three parts of government,",
"Principal Chief, Tribal Council, and Tribal Court.",

"(verse 1)",
"In North Carolina, tribes are free,",
"To have their own government, you see.",
"Three branches help them every day,",
"Working in their special way.",
"First, the Chief, who leads the way.",
"The Executive Branch works every day.",
"Making sure laws are in place.",
"Helping the tribe with a smiling face.",

"(chorus)",
"The Tribal Government, Tribal Government.",
"We’re free because we fought.",
"Three parts of government,",
"Principal Chief, Tribal Council, and Tribal Court.",

"(verse 2)",
"Next, the Tribal Council, a thoughtful team.",
"The Legislative Branch with a dream.",
"They write laws, both big and small.",
"Deciding what’s best for one and all.",
"Then the Tribal Court, so wise and fair.",
"The Judicial Branch is always there.",
"Solving problems, making peace.",
"Ensuring justice will never cease.",
"All three branches work as one,",
"For the tribe, they get things done.",
"Together they help the tribe to thrive.",
"Keeping traditions and culture alive.",

"(chorus)",
"The Tribal Government, Tribal Government.",
"We’re free because we fought.",
"Three parts of government,",
"Principal Chief, Tribal Council, and Tribal Court.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/tribalgovernment-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Barter--",
      subtitle1: "",
      standardNumber: "2.E.1",
      description: "Understand how the availability of resources impacts economic decisions.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fbarter2025video.mp4?alt=media&token=6345df74-466e-458e-b8c2-923c150215eb",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fbarter2025song.mp3?alt=media&token=8ac562f6-6b71-49da-9b5c-7044b49d009a",
      lyrics: ["(chorus)",
"As a farmer, I would barter.",
"As a farmer, I would barter.",

"(verse)",
"Most people did not have paper money.",
"Most people did not have coins.",
"Most people did not have paper money.",
"Most people did not have coins.",

"(chorus)",
"As a farmer, I would barter.",
"As a farmer, I would barter.",

"(verse)",
"Barter means trading, barter means trading,",
"Or exchanging of goods without using money.",
"Tobacco was used as money.",
"You could trade tobacco for goods and services.",

"(chorus)",
"As a farmer, I would barter.",
"As a farmer, I would barter.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/barter-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Trail of Tears--",
      subtitle1: "",
      standardNumber: "2.G.1",
      description: "Understand how interaction between humans and the physical environment is impacted by movement and settlement.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Ftrailoftears2025video.mp4?alt=media&token=dcb46d5d-1824-4851-8762-e273899cb7ab",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Ftrailoftears2025song.mp3?alt=media&token=50b64742-10fd-41b9-97b8-1f3fa54d43d9",
      lyrics: ["(Chorus)",
"The Cherokee, Creek, Seminole, Trail of Tears.",
"The Chickasaw, Choctaw, Trail of Tears.",

"(Verse 1)",
"In the 1830s, long ago,",
"Native tribes were told to go.",
"Cherokee, Choctaw, Creek, and more,",
"Forced to leave their home's front door.",
"The Indian Removal Act was made.",
"Their happy homes began to fade.",
"President Jackson gave the call,",
"Move westward now, leave it all.",
"Through forests deep and rivers wide,",
"Many walked and many cried.",
"On a path of pain and fears.",
"This journey known as Trail of Tears.",

"(Chorus)",
"The Cherokee, Creek, Seminole, Trail of Tears.",
"The Chickasaw, Choctaw, Trail of Tears.",

"(Verse 2)",
"Through rain and cold, without enough,",
"The journey was so very tough.",
"Many lost their lives that day,",
"As they were forced to walk away.",
"We remember their brave fight.",
"Their courage shining bright.",
"The Trail of Tears, a tale of sorrow,",
"Honors their hope for a better tomorrow.",

"(Chorus)",
"The Cherokee, Creek, Seminole, Trail of Tears.",
"The Chickasaw, Choctaw, Trail of Tears.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/trailoftears-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Forms of Water--",
      subtitle1: "",
      standardNumber: "2.G.1",
      description: "Understand how interaction between humans and the physical environment is impacted by movement and settlement.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fformsofwater2025video.mp4?alt=media&token=8973ee2c-2006-494e-bb3c-f6efcced57c6",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fformsofwater2025song.mp3?alt=media&token=9d23fd03-76b0-48e2-af88-c06d9d9d70ce",
      lyrics: ["(Verse)",
"Reservoir, a lake or tank used to collect water.",
"Reservoir, a lake or tank used to store water.",
"Groundwater, water that is beneath Earth’s surface.",
"Groundwater, water that is beneath Earth’s surface.",
"River, large stream of water that moves.",
"River, large stream of water that moves.",
"Lake, a body of water surrounded by land.",
"Lake, a body of water surrounded by land.",
"Bay, a body of water partially enclosed by land.",
"Bay, a body of water partially enclosed by land.",
"Peninsula, piece of land surrounded by water on three sides.",
"Peninsula, piece of land surrounded by water on three sides.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/formsofwater-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Benjamin Franklin--",
      subtitle1: "",
      standardNumber: "2.H.1",
      description: "Understand how various people and events have shaped America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fbenjaminfranklin2025video.mp4?alt=media&token=d3b4d87c-b523-4e1f-89f8-3a030ae0ad62",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fbenjaminfranklin2025song.mp3?alt=media&token=931e278a-40ea-405d-acae-a99e4cc1951d",
      lyrics: ["(Chorus)",
"Benjamin Franklin.",
"Benjamin Franklin.",
"Benjamin Franklin.",
"Benjamin Franklin.",

"(Verse 1)",
"Benjamin Franklin invented bifocals.",
"Bifocals or glasses, if you don't know.",
"Benjamin wanted to prove that lightning is electricity.",
"So, Benjamin conducted a famous kite experiment with a key.",
"Benjamin helped to start America's first library.",
"He helped to put streetlights and paved roads in Philadelphia, the city.",

"(Chorus)",
"Benjamin Franklin.",
"Benjamin Franklin.",

"(Verse 2)",
"The Declaration of Independence was partly written by Benjamin.",
"Benjamin also signed the United States Constitution.",
"Benjamin Franklin was a scientist and an inventor.",
"Benjamin Franklin was a writer and a leader.",
"Benjamin Franklin’s face is on the hundred bill I spent.",
"What Benjamin Franklin was not? He was not President.",

"(Chorus)",
"Benjamin Franklin.",
"Benjamin Franklin.",
"Benjamin Franklin.",
"Benjamin Franklin.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/benjaminfranklin-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Wright Brothers--",
      subtitle1: "",
      standardNumber: "2.H.1",
      description: "Understand how various people and events have shaped America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fthewrightbrothers2025video.mp4?alt=media&token=d24b5bd4-e204-468d-a42b-0cf1fa2c4d73",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fthewrightbrothers2025song.mp3?alt=media&token=0c782f3c-a4a3-4770-90c9-af6a81cf1e95",
      lyrics: ["(Chorus)",
"They soared, they flew, they changed the sky,",
"Kitty Hawk, where dreams could fly!",
"Wright Brothers made history,",
"Now we fly because they believed!",

"(Verse)",
"December winds, a plane took flight,",
"Twelve short seconds, oh what a sight!",
"From the sand into the air,",
"First in flight, they led us there.",

"(Chorus)",
"They soared, they flew, they changed the sky,",
"Kitty Hawk, where dreams could fly!",
"Wright Brothers made history,",
"Now we fly because they believed!",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/wrightbrothers-game2",
      titleImage: "", // Empty string means no image for Force
    },
    {
      title: "--Dr. Martin Luther King Jr.--",
      subtitle1: "",
      standardNumber: "2.H.1",
      description: "Understand how various people and events have shaped America.",
      video: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fdrmartinlutherkingjr2025video.mp4?alt=media&token=df730bc0-7199-4a8b-bf51-509505e2a40c",
      music: "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/music%2Fdrmartinlutherking2025song.mp3?alt=media&token=cfe6a225-2726-4001-8161-8d5dcff1c13d",
      lyrics: ["(Chorus)",
"Dr. Martin Luther King Jr.",
"Nobel Peace Prize winner.",
"Dr. Martin Luther King Jr.",
"Nobel Peace Prize winner.",
"Dr. Martin Luther King Jr.",
"Fought for equal rights.",
"Dr. Martin Luther King Jr.",
"Fought for equal rights.",

"(Verse)",
"Dr. Martin Luther King Jr. was an African American man.",
"His father was a minister and Martin was a good student.",
"He was born in 1929 on January 15th.",
"He began college when he was only 15.",
"Dr. Martin Luther King Jr. became a minister just like his dad.",
"He worked hard to change laws that were unfair.",
"Dr. Martin Luther King Jr. gave his famous speech, “I Have a Dream.”",
"He was killed one day because he fought for what he believed.",

"(Chorus)",
"Dr. Martin Luther King Jr.",
"Nobel Peace Prize winner.",
"Dr. Martin Luther King Jr.",
"Nobel Peace Prize winner.",
"Dr. Martin Luther King Jr.",
"Fought for equal rights.",
"Dr. Martin Luther King Jr.",
"Fought for equal rights.",

"© Smart Board Learning, LLC. All rights reserved.",],
      game: "games/nc-games/drmartinlutherkingjr-game2",
      titleImage: "", // Empty string means no image for Force
    },

  ];

  // Extract all unique standardNumbers from topics in order of appearance
  const uniqueStandardNumbers = Array.from(
    new Set(topics.map(topic => topic.standardNumber))
  );

  // Map each unique standardNumber to a color based on position
  const standardNumberColorMap = {};
  uniqueStandardNumbers.forEach((number, index) => {
    const colorIndex = index % styles.topicColors.length;
    standardNumberColorMap[number] = styles.topicColors[colorIndex];
  });

  return (
    <div
      style={{
        ...styles.container,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        background: backgroundImage
          ? undefined
          : "linear-gradient(to bottom, #6dd5fa, #2980b9)", // Attractive red to pink gradient
      }}
    >
      <img
        src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
        alt="Logo"
        style={styles.logo}
      />
      <div style={styles.navButtons}>
        <button
          onClick={handleHomeClick}
          style={styles.homeButton} // Purple color
        >
          Home
        </button>
        <button
          onClick={() => handleSubjectClick("science")}
          style={styles.scienceButton} // Blue color
        >
          Science
        </button>
        <button
          onClick={() => handleSubjectClick("socialstudies")}
          style={styles.socialStudiesButton} // Green color
        >
          Social Studies
        </button>
        <button
          onClick={() => handleSubjectClick("math")}
          style={styles.mathButton} // Reddish color
        >
          Math
        </button>
        <button
          onClick={() => handleSubjectClick("other")}
          style={styles.otherButton} // Yellowish color
        >
          Other
        </button>
      </div>
      <h1 style={styles.title}>
  {user && user.firstName && user.lastName ? (
    <>
      Georgia 4th Grade Social Studies
      <br />
      {user.sirName} {user.lastName}'s Class
    </>
  ) : (
    "Georgia 4th Grade Social Studies Educator Portal"
  )}
</h1>

      <p style={styles.subtitle}></p>
      <p style={styles.subtitle}>Watch the video or listen to the song to explore new concepts,<br></br> then play the game to reinforce learning in a fun way!<br></br><br></br> Educators can request custom materials, adjust content settings,<br></br> and track student progress below.</p> 
      
     {/* Topics */}
     <div style={styles.topicsContainer}>
     {topics.map((topic, index) =>
  visibleTopics[index] && topic.title?.trim() ? (
            <div key={index} style={{ 
              ...styles.topicCard, 
              borderColor: standardNumberColorMap[topic.standardNumber] || "#ccc" 
            }}>
              <div style={styles.topicContent}>
              {topic.titleImage ? (
  <>
    <img
      src={topic.titleImage}
      alt={topic.title}
      style={styles.titleImage}
    />
    <h3 style={styles.topicTitle}>{topic.title}</h3>
  </>
) : (
  <h3 style={styles.topicTitle}>{topic.title}</h3>
)}

               {topic.subtitle1 && <p style={styles.subtitle1}>{topic.subtitle1}</p>}
                <p style={styles.subtitle2}>Topic: {index + 1}</p>   
                <p style={styles.topicDescription}>
  <span style={styles.standardNumber}>{topic.standardNumber}</span> {topic.description}
</p>

              </div>
              <div style={styles.topicButtons}>
              {topic.video && (
  <button
    style={styles.button}
    onClick={() =>
      handleOpenPopup(
        <div style={styles.mediaStage}>
          <video
            src={topic.video}
            controls
            style={styles.media}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )
    }
  >
    <img
      src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fvideo-topic-image-button2.png?alt=media&token=c787b8ef-9cfa-4051-ae09-9d58cee36987" // Example icon
      alt="Video"
      style={{ width: "20px", height: "20px", marginRight: "8px" }}
    />
    Video
  </button>
)}
{topic.music && (
  <button
    style={styles.button}
    onClick={() =>
      handleOpenPopup(
        <div>
          <audio
            src={topic.music}
            controls
            style={styles.mediaAudio}
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            onContextMenu={(e) => e.preventDefault()}
          />
          <div style={styles.lyricsContainer}>
            {topic.lyrics.map((line, idx) => (
              <p key={idx} style={styles.lyricLine}>
                {line}
              </p>
            ))}
          </div>
        </div>
      )
    }
  >
    🎵 Music
  </button>
)}
{topic.game && (
  <button style={styles.button} onClick={() => handleGameClick(topic.game)}>
    🎮 Game
  </button>
)}

              </div>
            </div>
          ) : null
        )}
      </div>

      {popupContent && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleClosePopup} style={styles.closeButton}>
              ✖
            </button>
            {popupContent}
          </div>
        </div>
      )}
      
      {/* Educator Controls Container */}
      {educatorControlsVisible && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleEducatorControlsClose} style={styles.closeButton}>
              ✖
            </button>
            <div style={styles.educatorControls}>
              <h2>Educator Controls</h2>
              <h3>Check all tools you want your student to see. Uncheck those you want to hide.</h3>
              <div style={styles.controlsGrid}>
                {topics.map((topic, index) => (
                  <div key={index} style={styles.controlItem}>
                    <label>
                      <input
                        type="checkbox"
                        checked={visibleTopics[index]}
                        onChange={() => handleCheckboxChange(index)}
                      />
                      {topic.title}
                    </label>
                  </div>
                ))}
              </div>
              <button onClick={handleSaveChanges} style={styles.saveButton}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buttons Container */}
      <div style={styles.buttonsContainer}>
        <button style={styles.educatorControlsButton} onClick={handleEducatorControlsClick}>
          Click Here for Educator Controls
        </button>
        <button style={styles.requestButton} onClick={handleRequestClick}>
          Click Here to Request Custom Material
        </button>
        <button style={styles.studentProgressButton} onClick={handleStudentProgressClick}>
          Click Here for Student Progress
        </button>
      </div>

      {/* Form Container */}
      {formVisible && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleFormClose} style={styles.closeButton}>
              ✖
            </button>
            <div style={styles.formContainer}>
              <h2>Custom Material Request</h2>
              <form>
                <div style={styles.formRow}>
                  <div style={styles.formField}>
                    <label>Sir Name:</label>
                    <select style={styles.input}>
                      <option>Mr.</option>
                      <option>Mrs.</option>
                      <option>Ms.</option>
                      <option>Miss</option>
                      <option>N/A</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>First Name:</label>
                    <input type="text" style={styles.input} />
                  </div>
                  <div style={styles.formField}>
                    <label>Last Name:</label>
                    <input type="text" style={styles.input} />
                  </div>
                  <div style={styles.formField}>
                    <label>Grade:</label>
                    <select style={styles.input}>
                      <option>1st</option>
                      <option>2nd</option>
                      <option>3rd</option>
                      <option>4th</option>
                      <option>5th</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>State:</label>
                    <select style={styles.input}>
                      <option>AL</option>
                      <option>AK</option>
                      <option>AZ</option>
                      <option>AR</option>
                      <option>CA</option>
                      <option>CO</option>
                      <option>CT</option>
                      <option>DE</option>
                      <option>DC</option>
                      <option>FL</option>
                      <option>GA</option>
                      <option>HI</option>
                      <option>ID</option>
                      <option>IL</option>
                      <option>IN</option>
                      <option>IA</option>
                      <option>KS</option>
                      <option>KY</option>
                      <option>LA</option>
                      <option>ME</option>
                      <option>MD</option>
                      <option>MA</option>
                      <option>MI</option>
                      <option>MN</option>
                      <option>MS</option>
                      <option>MO</option>
                      <option>MT</option>
                      <option>NE</option>
                      <option>NV</option>
                      <option>NH</option>
                      <option>NJ</option>
                      <option>NM</option>
                      <option>NY</option>
                      <option>NC</option>
                      <option>ND</option>
                      <option>OH</option>
                      <option>OK</option>
                      <option>OR</option>
                      <option>PA</option>
                      <option>RI</option>
                      <option>SC</option>
                      <option>SD</option>
                      <option>TN</option>
                      <option>TX</option>
                      <option>UT</option>
                      <option>VT</option>
                      <option>VA</option>
                      <option>WA</option>
                      <option>WV</option>
                      <option>WI</option>
                      <option>WY</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>Subject:</label>
                    <select style={styles.input}>
                      <option>Math</option>
                      <option>Science</option>
                      <option>Social Studies</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div style={styles.formField}>
                    <label>Style:</label>
                    <select style={styles.input}>
                      <option>Select Style</option>
                      <option>Light Rock</option>
                      <option>Hip Hop</option>
                      <option>Country</option>
                      <option>Latin</option>
                      <option>R&B</option>
                      <option>Any Style</option>
                    </select>
                  </div>
                </div>
                <p></p>
                <p></p>
                <div style={styles.formField}>
                  <label>Information / Facts:</label>
                  <textarea placeholder="Please provide all facts needed in bullets for new material" style={styles.textarea}></textarea>
                </div>
                <p></p>
                <div style={styles.formField}>
                  <label>Game Questions & Answers <br></br>(All questions begin with Q-, all answers begin with A-. <br></br>The multiple choice correct answer begins with *.):</label>
                  <textarea placeholder="Please provide all games questions and answer." style={styles.textarea}></textarea>
                </div>
                <p>
                  Please allow 7-14 days for the material to be added to your portal. All custom material is protected under the Terms of Use. Payment for custom material allows the user to use the custom material within their portal until canceled.
                </p>
                <div>
                  <button type="button" onClick={() => window.open("https://example.com/pay", "_blank")} style={styles.payButton}>
                    Pay Now
                  </button>
                  <button type="button" onClick={handleFormClose} style={styles.cancelButton}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <footer style={styles.footer}>
        <p>Smart Board Learning & Freinds</p>
        <p>Trademark and copyright © 2018-2025</p>
      </footer>
    </div>
  );
};

const baseNavButton = {
  color: "#fff",
  padding: "10px 20px",
  fontSize: "25px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "200px",
  height: "200px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
  border: "2px solid #222", // Border for depth
  transition: "transform 0.2s, box-shadow 0.2s",
};

const styles = {
  container: {
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    minHeight: "100vh", // Ensure the background covers the full viewport height
    padding: "20px",
    backgroundSize: "cover", // Ensure the background image or gradient covers the entire container
    backgroundPosition: "center",
    backgroundColor: "#f0f4f8", // Softer background color
    background: "radial-gradient(circle at 30% 30%, #d0eaff, #2980b9)", // Updated gradient
  },
  footer: {
    marginTop: "40px",
    fontSize: "20px",
    color: "#000",
    textAlign: "center",
  },
  logo: {
    width: "150px",
    margin: "0 auto",
    display: "block",
    transform: "rotate(-2deg)", // Slight tilt
    filter: "drop-shadow(2px 4px 2px rgba(0,0,0,0.4))", // Drop shadow for bounce effect
  },
  navButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    gap: "10px",
  },
  navButton: baseNavButton, // Base style for nav buttons
  homeButton: {
    ...baseNavButton,
    backgroundColor: "#A020F0", // Purple color
  },
  scienceButton: {
    ...baseNavButton,
    backgroundColor: "#4a90e2", // Blue color
  },
  socialStudiesButton: {
    ...baseNavButton,
    backgroundColor: "#4CAF50", // Green color
  },
  mathButton: {
    ...baseNavButton,
    backgroundColor: "#FF6347", // Reddish color
  },
  otherButton: {
    ...baseNavButton,
    backgroundColor: "#FFD700", // Yellowish color
  },
  title: {
    fontSize: "35px",
    color: "#000", // Change color to black
    marginTop: "20px",
  },
  subtitle: {
    fontSize: "20px",
    color: "#555", // Softer subtitle color
    marginBottom: "20px",
  },
  topicsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  },
  topicCard: {
    backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white background
    borderRadius: "10px",
    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)", // Layered shadow for depth
    padding: "20px",
    textAlign: "center",
    backgroundImage: `
      linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), 
      url('https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fsocialstudies-bg-texture-topic.png?alt=media&token=3d079085-acc9-402b-b3f2-a8019b456121')
    `, // Add faded overlay
    backgroundSize: "cover",
    backgroundBlendMode: "overlay", // Blend the gradient and image
    border: "5px solid", // Add border
  },
  topicTitle: {
    fontSize: "22px",
    color: "#333",
    marginBottom: "10px",
    fontFamily: "'Quicksand', sans-serif", // Playful font without cursive
  },
  topicContent: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  titleImage: {
    width: "100%",
    maxHeight: "150px",
    objectFit: "contain",
    marginBottom: "10px",
  },
  subtitle1: {
    fontSize: "18px",
    color: "#777", // Softer subtitle color
    fontStyle: "italic",
  },
  subtitle2: {
    fontSize: "14px",
    color: "#555",
  },
  topicDescription: {
    fontSize: "14px",
    color: "#555", // Softer description color
    textAlign: "center",
  },
  topicButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  button: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px",
    fontSize: "14px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    flex: "1",
    margin: "0 5px",
    boxShadow: "0 4px 0 #1e5aa8, 0 6px 10px rgba(0,0,0,0.25)", // Blue shadow
    border: "2px solid #1e5aa8", // Blue border
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  ":hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 12px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
  },
  ":active": {
    transform: "translateY(0)",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)", // Subtle shadow on click
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popupBox: {
    // Without this a long lyric sheet pushes the box off-screen. Content
    // scrolls inside the popup now; the popup itself never exceeds the
    // viewport.
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)", // Softer shadow
    position: "relative",
    maxWidth: "600px",
    width: "90%",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "18px",
    cursor: "pointer",
    textAlign: "center",
    lineHeight: "30px",
  },
  // A fixed 16:9 stage. Every video renders in an identically sized frame
  // regardless of its source dimensions — a portrait clip gets black bars on
  // the sides instead of stretching the popup taller than the screen, which
  // is what forced people to scroll.
  mediaStage: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "10px",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    border: "none",
    background: "#000",
  },
  // Audio has no intrinsic height, so it keeps a simple full-width rule.
  mediaAudio: {
    width: "100%",
    marginTop: "10px",
  },
  lyricsContainer: {
    marginTop: "20px",
    textAlign: "center",
    maxHeight: "500px", // Set a maximum height
    overflowY: "auto", // Enable vertical scrolling
  },
  lyricLine: {
    fontSize: "16px",
    color: "#333",
    margin: "5px 0",
  },
  educatorControls: {
    backgroundColor: "#e0e7ef", // Softer background color for educator controls
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
  },
  controlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
  },
  controlItem: {
    textAlign: "left",
  },
  formContainer: {
    backgroundColor: "#f9f9f9",
    padding: "30px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 2)",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "30px",
  },
  formField: {
    flex: "1 1 200",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  input: {
    width: "80%",
    padding: "10px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "5px",
  },
  textarea: {
    width: "80%",
    padding: "10",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "10px",
    minHeight: "100px",
  },
  payButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
  },
  requestButton: {
    backgroundColor: "#ffa500", // Orange color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #cc7000, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #b86e00", // Border for depth
    marginTop: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#ff4d4d",
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
    marginLeft: "10px",
  },
  educatorControlsButton: {
    backgroundColor: "#A020F0", // Purple color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #5e1280, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #4a0e60", // Border for depth
    marginTop: "20px",
    marginLeft: "10px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginTop: "20px",
  },
  studentProgressButton: {
    backgroundColor: "#ff69b4", // Pink color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #b4004b, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #8e0038", // Border for depth
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px", // Add marginTop to align the button
  },
  studentProgress: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  studentButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
  },
  studentButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#000",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
  },
  saveButton: {
    backgroundColor: "linear-gradient(to bottom, #4caf50, #388e3c)", // Match button style
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 4px 0 #2e7d32, 0 6px 10px rgba(0,0,0,0.25)", // Layered shadow
    border: "2px solid #1b5e20", // Border for depth
    marginTop: "20px",
  },
  standardNumber: {
    fontWeight: "bold",
    color: "#222",
    marginRight: "5px",
  },
  topicColors: [
    "#FF6347",  // Reddish
    "#4CAF50",  // Green
    "#FFD700",  // Yellow
    "#4a90e2",  // Blue
    "#A020F0",  // Purple
    "#FF69B4",  // Pink
    "#FFA500",  // Orange
    "#2980b9",  // Deep Blue
    "#00CED1",  // Dark Turquoise (new)
    "#8B4513",  // Saddle Brown (new)
  ],
};

export default GA4thEducatorSocialStudies;
